import { normalizeSignInCode, SIGN_IN_CODE_LENGTH, type SignInResult } from "@serophin/shared";
import { useEffect, useId, useRef } from "preact/hooks";
import { DEFAULT_SIGN_IN_SKIN, type SignInSkin } from "./signInSkin";
import { type EmailCodeSignIn, type EmailCodeSignInStep, useEmailCodeSignIn } from "./useEmailCodeSignIn";

export type EmailCodeFormProps = {
  onSignedIn: (result: SignInResult) => void | Promise<void>;
  baseUrl?: string;
  next?: string;
  initialEmail?: string;
  skin?: SignInSkin;
  onStepChange?: (step: EmailCodeSignInStep) => void;
};

type StepProps = {
  signIn: EmailCodeSignIn;
  skin: SignInSkin;
};

const inputClass = (skin: SignInSkin, error: string | null) => (error ? `${skin.input} ${skin.inputError}` : skin.input);

const SignInError = ({ error, skin }: { error: string | null; skin: SignInSkin }) =>
  error ? (
    <p role="alert" class={skin.error}>
      {error}
    </p>
  ) : null;

const SubmitButton = ({ signIn, skin, label }: StepProps & { label: string }) => (
  <button type="submit" class={skin.primaryButton} disabled={signIn.busy} aria-busy={signIn.busy}>
    {label}
  </button>
);

const EmailStep = ({ signIn, skin }: StepProps) => {
  const id = useId();
  return (
    <>
      <label htmlFor={id} class={skin.label}>
        Email
      </label>
      <input
        id={id}
        type="email"
        autocomplete="email"
        placeholder="you@example.com"
        value={signIn.email}
        aria-invalid={signIn.error !== null}
        class={inputClass(skin, signIn.error)}
        onInput={(event) => signIn.setEmail(event.currentTarget.value)}
      />
      <SignInError error={signIn.error} skin={skin} />
      <SubmitButton signIn={signIn} skin={skin} label="Continue with email" />
    </>
  );
};

const CodeInput = ({ signIn, skin, id }: StepProps & { id: string }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  const handleInput = (target: HTMLInputElement) => {
    const code = normalizeSignInCode(target.value);
    target.value = code;
    signIn.setCode(code);
  };
  return (
    <input
      ref={ref}
      id={id}
      type="text"
      inputMode="numeric"
      autocomplete="one-time-code"
      maxLength={SIGN_IN_CODE_LENGTH}
      placeholder="123456"
      value={signIn.code}
      aria-invalid={signIn.error !== null}
      class={inputClass(skin, signIn.error)}
      onInput={(event) => handleInput(event.currentTarget)}
    />
  );
};

const CodeLinks = ({ signIn, skin }: StepProps) => (
  <div class="flex items-center justify-between gap-3">
    <button
      type="button"
      class={skin.linkButton}
      disabled={signIn.busy || signIn.resendIn > 0}
      onClick={() => {
        void signIn.resend();
      }}
    >
      {signIn.resendIn > 0 ? `Resend in ${signIn.resendIn}s` : "Resend code"}
    </button>
    <button type="button" class={skin.linkButton} disabled={signIn.busy} onClick={signIn.changeEmail}>
      Use a different email
    </button>
  </div>
);

const CodeStep = ({ signIn, skin }: StepProps) => {
  const id = useId();
  return (
    <>
      <label htmlFor={id} class={skin.label}>
        Code sent to {signIn.email}
      </label>
      <CodeInput signIn={signIn} skin={skin} id={id} />
      <SignInError error={signIn.error} skin={skin} />
      <SubmitButton signIn={signIn} skin={skin} label="Sign in" />
      <CodeLinks signIn={signIn} skin={skin} />
    </>
  );
};

const useStepChange = (step: EmailCodeSignInStep, onStepChange?: (step: EmailCodeSignInStep) => void) => {
  const callback = useRef(onStepChange);
  callback.current = onStepChange;
  useEffect(() => {
    callback.current?.(step);
  }, [step]);
};

export const EmailCodeForm = ({ onSignedIn, baseUrl, next, initialEmail, skin = DEFAULT_SIGN_IN_SKIN, onStepChange }: EmailCodeFormProps) => {
  const signIn = useEmailCodeSignIn({ onSignedIn, baseUrl, next, initialEmail });
  useStepChange(signIn.step, onStepChange);

  const handleSubmit = (event: Event) => {
    event.preventDefault();
    void (signIn.step === "email" ? signIn.submitEmail() : signIn.submitCode());
  };

  return (
    <form class="flex w-full flex-col gap-3" noValidate onSubmit={handleSubmit}>
      {signIn.step === "email" ? <EmailStep signIn={signIn} skin={skin} /> : <CodeStep signIn={signIn} skin={skin} />}
    </form>
  );
};
